import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

function resolveAccent(series, accentColor) {
    if (series?.accent) return 'accent-' + series.accent;
    const src = series?.accentColor || accentColor || '';
    const match = src.match(/bg-(\w+)-/);
    const map = {
        emerald: 'emerald', blue: 'sky', violet: 'violet', amber: 'amber',
        rose: 'rose', cyan: 'teal', indigo: 'violet', red: 'rose',
        teal: 'teal', orange: 'amber', pink: 'pink',
    };
    return 'accent-' + (map[match?.[1]] || 'sky');
}

function stageClass(stageOrStatus) {
    const v = (stageOrStatus || '').toLowerCase();
    if (v === 'final') return 'status-final';
    if (v.includes('draft')) return 'status-draft-1';
    if (v === 'coming soon') return 'status-coming-soon';
    if (v) return 'status-work-in-progress';
    return '';
}

export default function BookModal({ isOpen, onClose, book, series, accentColor, isGuest = false, onSignIn }) {
    if (!book) return null;

    const accentCls = resolveAccent(series, accentColor);
    const isLocked = book.loginRequired || book.gated;
    const isComingSoon = book.comingSoon;
    const stage = book.stage || book.status || '';

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className="themed-modal-overlay"
                        style={{ zIndex: 210 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onClose}
                    />
                    <motion.div
                        className={`themed-modal ${accentCls}`}
                        style={{ zIndex: 211, maxWidth: 520 }}
                        initial={{ opacity: 0, scale: 0.97, x: '-50%', y: 'calc(-50% + 16px)' }}
                        animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
                        exit={{ opacity: 0, scale: 0.97, x: '-50%', y: 'calc(-50% + 16px)' }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                    >
                        <div className="themed-modal-accent-bar" />
                        <div className="themed-modal-body">
                            <button
                                className="themed-modal-close"
                                onClick={onClose}
                                aria-label="Close"
                            >
                                ✕
                            </button>

                            <h2 className="themed-modal-title" style={{ marginBottom: 10 }}>
                                {book.title}
                            </h2>

                            <div style={{ marginBottom: 20 }}>
                                {isLocked ? (
                                    <span className="status-pill status-login-required">Login required</span>
                                ) : isComingSoon ? (
                                    <span className="status-pill status-coming-soon">Coming soon</span>
                                ) : stage ? (
                                    <span className={'status-pill ' + stageClass(stage)}>{stage}</span>
                                ) : null}
                            </div>

                            {book.description && (
                                <>
                                    <p className="themed-modal-section-label">Description</p>
                                    <p className="themed-modal-text">{book.description}</p>
                                </>
                            )}

                            <div className="modal-cta-row">
                                {isLocked ? (
                                    <button className="modal-cta-btn" onClick={onSignIn}>
                                        Sign in to read →
                                    </button>
                                ) : isComingSoon ? (
                                    <span className="modal-cta-btn secondary">Coming soon</span>
                                ) : book.link ? (
                                    <a
                                        href={book.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="modal-cta-btn"
                                    >
                                        {isGuest ? 'Read sample' : 'Read book'} →
                                    </a>
                                ) : null}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
